import { CogOrtho, SatelliteFlight, SatelliteMosaic, Coordinate } from "@nova/protobuf"
import { SatelliteSensorType } from "@nova/constants"
import { compareFractionalIndices } from "@nova/serializer/src/client/util/fractional_indexing"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectVisibleSatelliteFlights } from "features/entity-caching/flights/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { CogOrthoLayer } from "plugins/Orthomosaic/layers/CogOrthoLayer"
import { createHueColorizeTransform } from "plugins/Orthomosaic/layers/hueColorize"
import { selectOrthoOpacityMap } from "plugins/Orthomosaic/OrthomosaicSlice"
import { selectOrthoComparison } from "plugins/Orthomosaic/OrthoComparisonSlice"
import { selectOrthoMaskEditingFlightId } from "plugins/Orthomosaic/OrthoMaskSlice"
import { selectOrthoMasks } from "features/entity-caching/ortho-masks/selectors"

const RADAR_HUE_TRANSFORM = createHueColorizeTransform("#8da2ff", 0.15)

export const hueTransformForSensor = (sensorType: SatelliteSensorType | undefined) => {
    if (sensorType === SatelliteSensorType.Radar) return RADAR_HUE_TRANSFORM
    return undefined
}

export const cogOrthoBbox = (cogOrtho: CogOrtho): Coordinate[] => [
    { longitude: cogOrtho.minLng, latitude: cogOrtho.maxLat },
    { longitude: cogOrtho.maxLng, latitude: cogOrtho.maxLat },
    { longitude: cogOrtho.maxLng, latitude: cogOrtho.minLat },
    { longitude: cogOrtho.minLng, latitude: cogOrtho.minLat },
]

export const hasProcessingOption = (mosaic: SatelliteMosaic, option: "cloudfree" | "dehaze") =>
    mosaic.processingOptions.includes(option)

export const selectActiveMosaic = (flight: SatelliteFlight): SatelliteMosaic | undefined =>
    flight.mosaic.find(
        (mosaic) =>
            hasProcessingOption(mosaic, "cloudfree") === !!flight.cloudFree &&
            hasProcessingOption(mosaic, "dehaze") === !!flight.dehaze
    )

export const selectActiveCogOrtho = (flight: SatelliteFlight): CogOrtho | undefined =>
    selectActiveMosaic(flight)?.cogOrtho

type ProcessedSatelliteFlight = { flight: SatelliteFlight; cogOrtho: CogOrtho }

export const useSatelliteLayer = () => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const fireId = useAppSelector(selectActivatedFireId)
    const visibleFlights = useAppSelector(selectVisibleSatelliteFlights)
    const opacityMap = useAppSelector(selectOrthoOpacityMap)
    const comparison = useAppSelector(selectOrthoComparison)
    const orthoMasks = useAppSelector(selectOrthoMasks)
    const maskEditingFlightId = useAppSelector(selectOrthoMaskEditingFlightId)

    const allEntries = visibleFlights
        .map((flight) => ({ flight, cogOrtho: selectActiveCogOrtho(flight) }))
        .filter((entry): entry is ProcessedSatelliteFlight => !!entry.cogOrtho)
        .toSorted((a, b) => compareFractionalIndices(a.flight.index, b.flight.index))
    // The active comparison flight renders at OrthoComparisonBoost via useSatelliteComparisonBoostLayer
    const entries = comparison.flightId
        ? allEntries.filter((entry) => entry.flight.id !== comparison.flightId)
        : allEntries

    return entries.map(({ flight, cogOrtho }) => {
        const mask = orthoMasks.find((m) => m.flightId === flight.id)
        return CogOrthoLayer({
            // cogOrtho.fileId in the layer id forces a fresh layer on toggle instead of reusing a stale tile cache.
            id: `Satellite:${flight.id}:${cogOrtho.fileId}`,
            orgId: workspaceId || "",
            projectId: fireId || "",
            flightId: flight.demoId || flight.id,
            source: "satellite",
            cogId: cogOrtho.fileId,
            isDemo: !!flight.demoId,
            bbox: cogOrthoBbox(cogOrtho),
            maxZoom: cogOrtho.maxZoom || 21,
            minZoom: cogOrtho.minZoom || 8,
            opacity: opacityMap[flight.id] !== undefined ? opacityMap[flight.id] / 100 : 1,
            transform: hueTransformForSensor(flight.sensorType),
            maskPolygon:
                mask?.enabled && maskEditingFlightId !== flight.id
                    ? (mask.polygon?.coordinates ?? null)
                    : null,
        })
    })
}
